/**
 * @file: useOcrRecognize.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useFetchAsyncState } from '@euler/functions/useFetchAsyncState';
import { OcrRecognizeResult } from '@euler/services';
import { onErrorIgnore } from '@euler/utils';
import { useEffect, useRef } from 'react';

export const kMinBottomSheetHeight = 300;
export const kHandleHeight = 24;

export type OcrRecognizePreviewProps = {
  imageUri: string;
};

export const useOcrRecognize = <T, U>({
  imageUri,
  ...props
}: {
  recognize: (imageUriToRecognize: string) => Promise<OcrRecognizeResult<T>>;
  fetchExtraInfo: (result: T) => Promise<U>;
} & OcrRecognizePreviewProps) => {
  const recognizedResponse = useRef<OcrRecognizeResult<T>>();
  const [recognizeState, startRecognize] = useFetchAsyncState(
    async () => {
      const response = await props.recognize(imageUri);
      recognizedResponse.current = response;
      return response.result;
    },
    {
      status: 'loading',
    },
  );

  const [extraInfoState, fetchExtraInfo] = useFetchAsyncState(
    props.fetchExtraInfo,
    undefined,
  );

  useEffect(() => {
    startRecognize()
      .then(async result => {
        if (result) {
          await fetchExtraInfo(result);
        }
      })
      .catch(onErrorIgnore);
  }, [startRecognize, fetchExtraInfo]);

  return {
    recognizeState,
    extraInfoState,
    startRecognize,
    fetchExtraInfo,
    recognizedResponse: recognizedResponse.current,
  };
};
